import styles from "./Faq.module.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import Spinner from "../Spinner/Spinner";
import { Link } from "react-router-dom";
import { FaPlus } from "react-icons/fa";

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { RxDragHandleHorizontal } from "react-icons/rx";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "react-toastify";

function SortableRow({ faq }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: faq._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className={styles.clientrow}>
      <td>
        <DragHandle listeners={listeners} attributes={attributes} />
      </td>
      <td>
        <div className={styles.textEllipsis}>{faq.question}</div>
      </td>
      <td>
        <div className={styles.textEllipsis}>{faq.answer}</div>
      </td>
      <td className={styles.edit}>
        <Link to={`/faqs/${faq._id}/edit`}>
          <div>Edit</div>
        </Link>
      </td>
    </tr>
  );
}

const DragHandle = ({ listeners, attributes }) => {
  return (
    <span
      {...listeners}
      {...attributes}
      style={{
        cursor: "grab",
        padding: "8px",
        display: "inline-block",
        fontSize: "18px",
        userSelect: "none",
      }}
    >
      <RxDragHandleHorizontal />
    </span>
  );
};

export const Faq = () => {
  const [faqs, setFaqs] = useState([]);
  const [spin, setSpin] = useState(true);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isChanged, setIsChanged] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const getData = async () => {
    try {
      const response = await axios.get(`${API}/Admin/faqs`);
      const faqsData = response.data.faqs || [];
      const sorted = faqsData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setFaqs(sorted);
      setOriginalOrder(sorted.map((f) => f._id));
      setSpin(false);
    } catch (error) {
      console.log(error);
      setSpin(false);
    }
  };

  const reorderFaqs = async (order) => {
    try {
      const response = await axios.put(`${API}/Admin/faqs/reorder`, { order });
      await getData();
      toast.success("Order saved successfully");
    } catch (error) {
      console.log(error);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setFaqs((prev) => {
      const oldIndex = prev.findIndex((item) => item._id === active.id);
      const newIndex = prev.findIndex((item) => item._id === over.id);

      const newArray = arrayMove(prev, oldIndex, newIndex);

      newArray.forEach((item, idx) => {
        item.order = idx + 1;
      });

      const newOrderIds = newArray.map((f) => f._id);

      setIsChanged(
        JSON.stringify(newOrderIds) !== JSON.stringify(originalOrder)
      );

      return [...newArray];
    });
  };

  const handleSave = () => {
    const payload = faqs.map((item) => ({
      faqId: item._id,
      order: item.order,
    }));

    reorderFaqs(payload);

    setOriginalOrder(faqs.map((f) => f._id));
    setIsChanged(false);
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      {spin ? (
        <Spinner />
      ) : (
        <div className={styles.entirepage}>
          <div className={styles.heading}>FAQs</div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div className={styles.addingbutton}>
              <Link to="/faqs/add">
                <div className={styles.button}>
                  <FaPlus className={styles.addicon} />
                  Add FAQ
                </div>
              </Link>
            </div>

            <button
              onClick={handleSave}
              disabled={!isChanged}
              className={styles.button}
              style={{ width: "fit-content" }}
            >
              Save Order
            </button>
          </div>
          <table className={styles.displaytable}>
            <thead>
              <tr className={styles.clientrowhead}>
                <th></th>
                <th>Question</th>
                <th>Answer</th>
                <th>Edit</th>
              </tr>
            </thead>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={faqs.map((f) => f._id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {faqs.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        No FAQs found
                      </td>
                    </tr>
                  ) : (
                    faqs.map((faq) => <SortableRow key={faq._id} faq={faq} />)
                  )}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>
      )}
    </>
  );
};
